"use client";

import React from "react";
import { Cpu, PhoneCall, FileCheck, Wrench, Rocket, ArrowRight, Activity, Clock, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  label: "INITIAL ASSESSMENT",
  heading: "Case Qualification",
  description: "A comprehensive review of your timeshare contract and resort history to determine legal exit eligibility.",
  icon: FileCheck,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "02",
  label: "STRATEGY SESSION",
  heading: "Legal Architecture",
  description: "Detailed consultation with our specialists to map out a personalized strategy for permanent contract termination.",
  icon: PhoneCall,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "03",
  label: "FILE PREPARATION",
  heading: "Strategic Sign-off",
  description: "Formal documentation of the exit protocol, ensuring all legal grounds for misrepresentation are clearly identified.",
  icon: Activity,
  align: "left",
  colSpan: "md:col-span-5 md:col-start-2"
}, {
  id: "04",
  label: "ADVOCACY PHASE",
  heading: "Resort Negotiation",
  description: "Our legal advocacy team engages directly with the resort to resolve disputes and secure your release.",
  icon: Wrench,
  align: "right",
  colSpan: "md:col-span-5 md:col-start-7"
}, {
  id: "05",
  label: "FINAL RESOLUTION",
  heading: "Contract Termination",
  description: "Verification of your successful exit, confirming you are free from all future fees and ownership burdens.",
  icon: Rocket,
  align: "center",
  colSpan: "md:col-span-8 md:col-start-3"
}];
const protocolFeatures = [{
  title: "Permanent Release",
  description: "A legally binding termination of your contract and all future financial obligations.",
  icon: Clock
}, {
  title: "Strategic Advocacy",
  description: "Expert negotiation teams focused on challenging complex resort contract structures.",
  icon: Cpu
}, {
  title: "Credit Protection",
  description: "Guidance throughout the process to help safeguard your financial standing and peace of mind.",
  icon: TrendingUp
}];
function StepCard({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="159520" data-index={index} ref={ref} className={`
        ${step.colSpan} 
        transition-all duration-1000 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
        ${index > 0 ? 'mt-8 md:mt-24' : ''}
      `}>
      <Card className="bg-white/80 dark:bg-card/80 backdrop-blur-md border-border shadow-lg shadow-primary/5 overflow-hidden relative group hover:border-secondary/40 transition-colors duration-500">
        {}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
        
        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline" className="font-mono text-secondary border-secondary/20 bg-secondary/5 tracking-widest">
              EXIT.{step.id}
            </Badge>
            <step.icon className="w-5 h-5 text-muted-foreground group-hover:text-secondary transition-colors duration-300" />
          </div>
          <div className="space-y-1">
            <p className="font-mono text-xs text-muted-foreground tracking-widest uppercase">
              {step.label}
            </p>
            <CardTitle className="text-2xl font-bold tracking-tight text-primary font-serif">
              {step.heading}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="relative z-10">
          <CardDescription className="font-default text-sm leading-relaxed text-muted-foreground/90">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: protocolRef,
    isVisible: isProtocolVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="eligibilityform" className="bg-background py-[160px] relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-24 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-6 font-mono tracking-widest uppercase bg-secondary/10 text-secondary border-secondary/20 backdrop-blur-sm">
            Proven Exit Strategy
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 text-primary font-serif">
            The Architecture of <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary">
              Contractual Freedom
            </span>
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl mx-auto">
            A systematic, five-phase protocol designed to liberate you from the burden of timeshare ownership and escalating maintenance fees.
          </p>
        </div>

        {}
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 mb-32">
          {}
          <div className="hidden md:block absolute left-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent -translate-x-1/2" />
          
          {steps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
        </div>

        {}
        <div ref={protocolRef} className={`transition-all duration-1000 delay-300 ${isProtocolVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <Separator className="bg-border mb-12" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-6">
              <div>
                <h3 className="text-2xl font-bold tracking-tight text-primary mb-2 font-serif">
                  The Nexus Standard
                </h3>
                <p className="font-mono text-sm text-secondary">
                  Engineered exclusively for Nexus Group clients.
                </p>
              </div>
              <p className="text-muted-foreground leading-relaxed font-default">
                Our approach is built with a singular focus: protecting owners from unfair contracts. We don't just provide advice; we engineer strategic pathways to financial independence.
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="#">
                  Check Your Eligibility
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {protocolFeatures.map((feature, index) => <div key={index} data-index={index} className="space-y-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <h4 className="font-bold text-primary font-serif">{feature.title}</h4>
                  <p className="font-default text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>)}
            </div>
          </div>
          
          <Separator className="bg-border mt-12" />
        </div>

      </div>
    </section>;
}